import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { hostname } from "../providers/api";
import { CommercialEmpty, CommercialError } from "./CommercialUi";

const euros = (minor) =>
  (Number(minor || 0) / 100).toFixed(2).replace(/\.00$/, "");
const pricingModels = [
  { value: "FLAT", label: "Fixed price" },
  { value: "PER_UNIT", label: "Per unit" },
  { value: "TIERED", label: "Progressive tiers" },
  { value: "LOG_INTERPOLATED", label: "SKU matrix (log interpolation)" },
];
const billingPeriods = [
  { value: "YEAR", label: "Yearly" },
  { value: "MONTH", label: "Monthly" },
  { value: "ONE_TIME", label: "One-time" },
];
const tierRows = (tiers = []) =>
  tiers.map((tier) => ({
    upTo: tier.upTo == null ? "" : String(tier.upTo),
    unitAmount: euros(tier.unitAmountMinor),
    amount: tier.amountMinor == null ? "" : euros(tier.amountMinor),
  }));
const featureSkuCode = (feature) =>
  String(feature?.key || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "_");
const pricingConfigurationError = (form) => {
  if (!form.code.trim() || !form.name.trim()) return "Service and SKU code are required.";
  if (!form.active) return null;
  const baseAmount = Number(form.baseAmount || 0);
  const unitAmount = Number(form.unitAmount || 0);
  if (form.pricingModel === "FLAT" && baseAmount <= 0)
    return "Add a positive base price before making this service available to Sales.";
  if (form.pricingModel === "PER_UNIT" && baseAmount <= 0 && unitAmount <= 0)
    return "Add a positive base or unit price before making this service available to Sales.";
  if (form.pricingModel === "TIERED") {
    if (!form.tiers.length) return "Add at least one price tier.";
    let previousLimit = 0;
    for (let index = 0; index < form.tiers.length; index += 1) {
      const tier = form.tiers[index];
      const isLast = index === form.tiers.length - 1;
      if (!isLast && tier.upTo === "") return "Only the last price tier can be unlimited.";
      if (tier.upTo !== "") {
        const limit = Number(tier.upTo);
        if (!Number.isFinite(limit) || limit <= previousLimit)
          return "Tier limits must be positive and strictly increasing.";
        previousLimit = limit;
      }
    }
    if (form.tiers[form.tiers.length - 1].upTo !== "")
      return "The last price tier must have no upper limit.";
    if (
      baseAmount <= 0 &&
      !form.tiers.some((tier) => Number(tier.unitAmount || 0) > 0)
    )
      return "Add a positive base or tier price before making this service available to Sales.";
  }
  if (form.pricingModel === "LOG_INTERPOLATED") {
    if (form.tiers.length < 2) return "Add at least two SKU price breakpoints.";
    let previousLimit = 0;
    for (const tier of form.tiers) {
      const limit = Number(tier.upTo);
      if (!Number.isFinite(limit) || limit <= previousLimit)
        return "SKU breakpoints must be positive and strictly increasing.";
      previousLimit = limit;
    }
    if (!form.tiers.some((tier) => Number(tier.amount || 0) > 0))
      return "Add at least one approved breakpoint price.";
  }
  return null;
};
const blankSku = {
  featureId: "",
  code: "",
  name: "",
  description: "",
  unitLabel: "unit",
  billingPeriod: "YEAR",
  pricingModel: "FLAT",
  baseAmount: "0",
  unitAmount: "0",
  includedUnits: 0,
  tiers: [],
  active: false,
  displayOrder: 0,
};

export default function CommercialPricingCatalogPanel({ token }) {
  const [catalogs, setCatalogs] = useState(null);
  const [features, setFeatures] = useState(null);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catalogForm, setCatalogForm] = useState(null);
  const [skuForm, setSkuForm] = useState(null);
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token],
  );
  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([
      fetch(`${hostname}/commercial/pricing-catalogs`, { headers }),
      fetch(`${hostname}/commercial/features`, { headers }),
    ])
      .then(async ([catalogResponse, featureResponse]) => {
        if (!catalogResponse.ok)
          throw new Error(`Pricing catalogs failed (${catalogResponse.status})`);
        if (!featureResponse.ok)
          throw new Error(`Platform services failed (${featureResponse.status})`);
        return Promise.all([catalogResponse.json(), featureResponse.json()]);
      })
      .then(([catalogValue, featureValue]) => {
        if (!cancelled) {
          setCatalogs(catalogValue);
          setFeatures(featureValue);
        }
      })
      .catch((cause) => {
        if (!cancelled) setError(cause);
      });
    return () => {
      cancelled = true;
    };
  }, [headers, revision]);
  const mutate = async (path, method, body) => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${hostname}/commercial/${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      setCatalogForm(null);
      setSkuForm(null);
      setRevision((value) => value + 1);
    } catch (cause) {
      setError(cause);
    } finally {
      setSaving(false);
    }
  };
  if (error && !catalogs)
    return (
      <CommercialError
        error={error}
        onRetry={() => setRevision((value) => value + 1)}
      />
    );
  if (!catalogs || !features)
    return (
      <Box py={4} textAlign="center">
        <CircularProgress size={24} />
      </Box>
    );
  const publishedCatalog = catalogs
    .filter((catalog) => catalog.status === "PUBLISHED")
    .sort((left, right) => Number(right.version) - Number(left.version))[0];
  const draftCatalog = catalogs
    .filter((catalog) => catalog.status === "DRAFT")
    .sort((left, right) => Number(right.version) - Number(left.version))[0];
  return (
    <>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gridGap={16}
        mb={2}
      >
        <div>
          <Typography variant="h6">Pricing catalogs</Typography>
          <Typography color="textSecondary">
            Draft rates can be edited. Publishing freezes that version and makes
            it authoritative for Sales quotes.
          </Typography>
        </div>
        {draftCatalog ? (
          <Button color="primary" variant="contained" disabled>
            Draft v{draftCatalog.version} in progress
          </Button>
        ) : publishedCatalog ? (
          <Button
            color="primary"
            variant="contained"
            disabled={saving}
            onClick={() =>
              mutate(`pricing-catalogs/${publishedCatalog.id}/clone`, "POST")
            }
          >
            Create next version
          </Button>
        ) : (
          <Button
            color="primary"
            variant="contained"
            onClick={() =>
              setCatalogForm({
                code: "BCOME_SALES",
                version: 1,
                name: "BCome Sales pricing",
                currency: "EUR",
                effectiveFrom: new Date().toISOString().slice(0, 10),
                effectiveUntil: "",
              })
            }
          >
            Create first catalog
          </Button>
        )}
      </Box>
      {error && (
        <Box mb={2}>
          <CommercialError
            error={error}
            onRetry={() => setRevision((value) => value + 1)}
          />
        </Box>
      )}
      <Grid container spacing={2}>
        {catalogs.map((catalog) => (
          <Grid item xs={12} key={catalog.id}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  flexWrap="wrap"
                  gridGap={12}
                >
                  <div>
                    <Typography variant="h6">{catalog.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {catalog.code} v{catalog.version} · {catalog.currency}
                    </Typography>
                  </div>
                  <Chip
                    label={catalog.status}
                    color={
                      catalog.status === "PUBLISHED" ? "primary" : "default"
                    }
                  />
                </Box>
                <Box mt={2} display="grid" gridGap={8}>
                  {(catalog.skus || []).map((sku) => (
                    <Box
                      key={sku.id}
                      role="group"
                      aria-label={`${sku.name} pricing configuration`}
                      display="grid"
                      borderTop="1px solid #eee"
                      pt={1}
                      gridGap={6}
                    >
                      <div>
                        <Typography variant="subtitle2">
                          {sku.name}{" "}
                          <Typography
                            component="span"
                            variant="caption"
                            color="textSecondary"
                          >
                            ({sku.code})
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {sku.pricingModel} · base €
                          {euros(sku.baseAmountMinor)} · unit €
                          {euros(sku.unitAmountMinor)} · {sku.billingPeriod} ·{" "}
                          {sku.active
                            ? "available to Sales"
                            : "pricing not configured"}
                        </Typography>
                      </div>
                      {catalog.status === "DRAFT" && (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          style={{ justifySelf: "start" }}
                          aria-label={`Edit pricing for ${sku.name}`}
                          onClick={() =>
                            setSkuForm({
                              catalogId: catalog.id,
                              ...sku,
                              baseAmount: euros(sku.baseAmountMinor),
                              unitAmount: euros(sku.unitAmountMinor),
                              featureId: sku.featureId || "",
                              tiers: tierRows(sku.tiers),
                            })
                          }
                        >
                          Edit pricing
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
                {catalog.status !== "DRAFT" && (
                  <Box mt={2}>
                    <Typography variant="caption" color="textSecondary">
                      Published versions are locked. Edit pricing in the current
                      draft, then publish the reviewed replacement.
                    </Typography>
                  </Box>
                )}
                {catalog.status === "DRAFT" && (
                  <Box mt={2} display="flex" gridGap={8}>
                    <Button
                      onClick={() =>
                        setSkuForm({ catalogId: catalog.id, ...blankSku })
                      }
                    >
                      Add pricing configuration
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      aria-label={`Publish ${catalog.name}`}
                      onClick={() =>
                        mutate(`pricing-catalogs/${catalog.id}/publish`, "POST")
                      }
                    >
                      Publish version
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {!catalogs.length && (
          <Grid item xs={12}>
            <CommercialEmpty
              title="No pricing catalog"
              description="Create the first draft version, add its services and publish it before Sales creates quotes."
            />
          </Grid>
        )}
      </Grid>
      {catalogForm && (
        <Dialog
          open
          fullWidth
          maxWidth="sm"
          onClose={() => setCatalogForm(null)}
        >
          <DialogTitle>New pricing version</DialogTitle>
          <DialogContent>
            <Box pt={1} display="grid" gridGap={16}>
              <TextField
                variant="outlined"
                label="Catalog code"
                value={catalogForm.code}
                onChange={(event) =>
                  setCatalogForm({ ...catalogForm, code: event.target.value })
                }
              />
              <TextField
                variant="outlined"
                type="number"
                label="Version"
                value={catalogForm.version}
                onChange={(event) =>
                  setCatalogForm({
                    ...catalogForm,
                    version: Number(event.target.value),
                  })
                }
              />
              <TextField
                variant="outlined"
                label="Name"
                value={catalogForm.name}
                onChange={(event) =>
                  setCatalogForm({ ...catalogForm, name: event.target.value })
                }
              />
              <TextField
                variant="outlined"
                label="Currency"
                value={catalogForm.currency}
                onChange={(event) =>
                  setCatalogForm({
                    ...catalogForm,
                    currency: event.target.value,
                  })
                }
              />
              <TextField
                variant="outlined"
                type="date"
                InputLabelProps={{ shrink: true }}
                label="Effective from"
                value={catalogForm.effectiveFrom}
                onChange={(event) =>
                  setCatalogForm({
                    ...catalogForm,
                    effectiveFrom: event.target.value,
                  })
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCatalogForm(null)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={saving || !catalogForm.code || !catalogForm.name}
              onClick={() => mutate("pricing-catalogs", "POST", catalogForm)}
            >
              Create draft
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {skuForm && (
        <Dialog open fullWidth maxWidth="sm" onClose={() => setSkuForm(null)}>
          <DialogTitle>
            {skuForm.id ? "Edit" : "Add"} pricing configuration
          </DialogTitle>
          <DialogContent>
            <Box pt={1} display="grid" gridGap={16}>
              <Typography color="textSecondary">
                Link a platform service to its commercial pricing rule. Draft
                changes are not used by Sales until this price book is published.
              </Typography>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="pricing-platform-service-label">
                  Platform service
                </InputLabel>
                <Select
                  label="Platform service"
                  labelId="pricing-platform-service-label"
                  inputProps={{ "aria-label": "Platform service" }}
                  value={skuForm.featureId}
                  onChange={(event) => {
                    const feature = features.find(
                      (item) => item.id === event.target.value,
                    );
                    setSkuForm({
                      ...skuForm,
                      featureId: event.target.value,
                      ...(!skuForm.id && feature
                        ? { code: featureSkuCode(feature), name: feature.name }
                        : {}),
                    });
                  }}
                >
                  <MenuItem value="">
                    <em>Custom commercial service</em>
                  </MenuItem>
                  {features.map((feature) => (
                    <MenuItem key={feature.id} value={feature.id}>
                      {feature.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                variant="outlined"
                label="SKU code"
                value={skuForm.code}
                onChange={(event) =>
                  setSkuForm({ ...skuForm, code: event.target.value })
                }
              />
              <TextField
                variant="outlined"
                label="Service name"
                value={skuForm.name}
                onChange={(event) =>
                  setSkuForm({ ...skuForm, name: event.target.value })
                }
              />
              <TextField
                variant="outlined"
                multiline
                minRows={2}
                label="Description"
                value={skuForm.description || ""}
                onChange={(event) =>
                  setSkuForm({ ...skuForm, description: event.target.value })
                }
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="pricing-model-label">Pricing model</InputLabel>
                    <Select
                      label="Pricing model"
                      labelId="pricing-model-label"
                      inputProps={{ "aria-label": "Pricing model" }}
                      value={skuForm.pricingModel}
                      onChange={(event) =>
                        setSkuForm({
                          ...skuForm,
                          pricingModel: event.target.value,
                        })
                      }
                    >
                      {pricingModels.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="billing-period-label">
                      Billing period
                    </InputLabel>
                    <Select
                      label="Billing period"
                      labelId="billing-period-label"
                      inputProps={{ "aria-label": "Billing period" }}
                      value={skuForm.billingPeriod}
                      onChange={(event) =>
                        setSkuForm({
                          ...skuForm,
                          billingPeriod: event.target.value,
                        })
                      }
                    >
                      {billingPeriods.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <TextField
                variant="outlined"
                type="number"
                label="Base price €"
                inputProps={{ "aria-label": "Base price €" }}
                value={skuForm.baseAmount}
                onChange={(event) =>
                  setSkuForm({ ...skuForm, baseAmount: event.target.value })
                }
              />
              {skuForm.pricingModel === "PER_UNIT" && (
                <>
                  <TextField
                    variant="outlined"
                    type="number"
                    label="Unit price €"
                    value={skuForm.unitAmount}
                    onChange={(event) =>
                      setSkuForm({ ...skuForm, unitAmount: event.target.value })
                    }
                  />
                </>
              )}
              {["PER_UNIT", "TIERED", "LOG_INTERPOLATED"].includes(skuForm.pricingModel) && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="number"
                      label={skuForm.pricingModel === "LOG_INTERPOLATED" ? "Minimum SKU quantity" : "Included units"}
                      value={skuForm.includedUnits}
                      onChange={(event) =>
                        setSkuForm({
                          ...skuForm,
                          includedUnits: event.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Unit label"
                      value={skuForm.unitLabel}
                      onChange={(event) =>
                        setSkuForm({ ...skuForm, unitLabel: event.target.value })
                      }
                    />
                  </Grid>
                </Grid>
              )}
              {["TIERED", "LOG_INTERPOLATED"].includes(skuForm.pricingModel) && (
                <Box display="grid" gridGap={12}>
                  <div>
                    <Typography variant="subtitle2">
                      {skuForm.pricingModel === "LOG_INTERPOLATED" ? "SKU price breakpoints" : "Progressive price tiers"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {skuForm.pricingModel === "LOG_INTERPOLATED" ? "Prices between breakpoints use logarithmic interpolation. Leave a price blank where Sales must request a custom quote." : "Define increasing volume limits. Leave the final limit blank so every higher volume is covered."}
                    </Typography>
                  </div>
                  {skuForm.tiers.map((tier, index) => (
                    <Grid container spacing={1} alignItems="flex-start" key={index}>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="number"
                          label={skuForm.pricingModel === "LOG_INTERPOLATED" ? `Breakpoint ${index + 1} units` : `Tier ${index + 1} up to units`}
                          inputProps={{
                            "aria-label": `Tier ${index + 1} up to units`,
                          }}
                          helperText={
                            skuForm.pricingModel === "TIERED" && index === skuForm.tiers.length - 1
                              ? "Leave blank for no upper limit"
                              : undefined
                          }
                          value={tier.upTo}
                          onChange={(event) => {
                            const tiers = [...skuForm.tiers];
                            tiers[index] = { ...tier, upTo: event.target.value };
                            setSkuForm({ ...skuForm, tiers });
                          }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="number"
                          label={skuForm.pricingModel === "LOG_INTERPOLATED" ? `Breakpoint ${index + 1} price €` : `Tier ${index + 1} unit price €`}
                          inputProps={{
                            "aria-label": skuForm.pricingModel === "LOG_INTERPOLATED" ? `Breakpoint ${index + 1} price €` : `Tier ${index + 1} unit price €`,
                          }}
                          value={skuForm.pricingModel === "LOG_INTERPOLATED" ? tier.amount : tier.unitAmount}
                          onChange={(event) => {
                            const tiers = [...skuForm.tiers];
                            tiers[index] = {
                              ...tier,
                              [skuForm.pricingModel === "LOG_INTERPOLATED" ? "amount" : "unitAmount"]: event.target.value,
                            };
                            setSkuForm({ ...skuForm, tiers });
                          }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          aria-label={`Remove tier ${index + 1}`}
                          onClick={() =>
                            setSkuForm({
                              ...skuForm,
                              tiers: skuForm.tiers.filter(
                                (_item, itemIndex) => itemIndex !== index,
                              ),
                            })
                          }
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Box>
                    <Button
                      onClick={() =>
                        setSkuForm({
                          ...skuForm,
                          tiers: [
                            ...skuForm.tiers,
                            { upTo: "", unitAmount: "0", amount: "" },
                          ],
                        })
                      }
                    >
                      Add price tier
                    </Button>
                  </Box>
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={Boolean(skuForm.active)}
                    onChange={(event) =>
                      setSkuForm({ ...skuForm, active: event.target.checked })
                    }
                  />
                }
                label="Available to Sales"
              />
              {pricingConfigurationError(skuForm) && (
                <Typography color="error" variant="body2">
                  {pricingConfigurationError(skuForm)}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSkuForm(null)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={saving || Boolean(pricingConfigurationError(skuForm))}
              onClick={() => {
                const tiers =
                  ["TIERED", "LOG_INTERPOLATED"].includes(skuForm.pricingModel)
                    ? skuForm.tiers.map((tier) => ({
                        upTo: tier.upTo === "" ? null : Number(tier.upTo),
                        unitAmountMinor: Math.round(
                          Number(tier.unitAmount || 0) * 100,
                        ),
                        ...(skuForm.pricingModel === "LOG_INTERPOLATED"
                          ? {
                              amountMinor:
                                tier.amount === ""
                                  ? null
                                  : Math.round(Number(tier.amount || 0) * 100),
                            }
                          : {}),
                      }))
                    : [];
                mutate(`pricing-catalogs/${skuForm.catalogId}/skus`, "PUT", {
                  ...skuForm,
                  baseAmountMinor: Math.round(
                    Number(skuForm.baseAmount || 0) * 100,
                  ),
                  unitAmountMinor: Math.round(
                    Number(skuForm.unitAmount || 0) * 100,
                  ),
                  tiers,
                });
              }}
            >
              Save pricing configuration
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
